// Localized consent state, one decision per purpose category. Persisted in a
// cookie scoped to the registrable domain so the choice is shared across hosts
// (www + foretag, tasks/foretag/014), with a localStorage mirror kept as the
// fallback for browsers where the cookie write is refused. Server-side reads
// are not supported on purpose: consent is a per-device, per-browser decision
// and we don't want it leaking into RSC cache keys (task 141).
//
// Why a cookie and not just localStorage: localStorage is origin-scoped, so a
// grant on www.markalappar.se was invisible on foretag.markalappar.se — while
// posthog-js sets its identity cookie on the eTLD+1, so *identity* already
// spanned the hosts and only *consent* did not. That asymmetry meant the
// company host could never link a visitor to a purchase.
//
// Categories:
//   analytics — PostHog product analytics, session replay, error tracking.
//   marketing — the TikTok and Meta advertising pixels and the Google Ads
//               gtag.js tag (ad measurement / retargeting / conversion
//               tracking).
// These are distinct GDPR purposes, so each has its own opt-in.
//
// Not every measurement tool needs a category: Vercel Web Analytics is
// cookieless and stores no identifier, so it counts as strictly necessary and
// loads unconditionally from the root layout with no gate here.

export type ConsentValue = "granted" | "denied";

export type ConsentCategory = "analytics" | "marketing";

export type ConsentState = Record<ConsentCategory, ConsentValue>;

export const CONSENT_CATEGORIES: readonly ConsentCategory[] = ["analytics", "marketing"];

// v2 stores a JSON object keyed by category. Bumped from v1 (a bare
// "granted"/"denied" string that covered analytics only) when the marketing
// category was added; `getConsent` migrates any surviving v1 value below.
export const CONSENT_STORAGE_KEY = "marka.consent.v2";
export const LEGACY_CONSENT_STORAGE_KEY = "marka.consent.analytics.v1";

// Same-tab notification channel. `storage` events fire in *other* tabs only,
// so we dispatch a CustomEvent on `window` for in-tab subscribers.
export const CONSENT_CHANGE_EVENT = "marka:consent-change";

export interface ConsentChangeDetail {
  state: ConsentState;
}

// One year. Long enough that a returning buyer is not re-prompted every
// season, short enough to stay a defensible retention period for a consent
// record.
const CONSENT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * The domain to scope the consent cookie to, or `null` for a host-only cookie.
 *
 * Derived from `NEXT_PUBLIC_SITE_URL` rather than guessed from the current
 * hostname, because guessing needs a public-suffix list: taking the last two
 * labels of a Vercel preview host yields `vercel.app`, which browsers reject
 * as a cookie domain — silently, leaving no consent stored at all.
 *
 * The domain attribute is set only when the current host actually sits under
 * the configured site domain. Anywhere else (previews, localhost, an
 * unconfigured env) the cookie stays host-only: consent still persists, it
 * just isn't shared across hosts, which is the right behaviour when we cannot
 * prove the two hosts are ours.
 */
export function resolveConsentCookieDomain(
  hostname: string,
  siteUrl: string | undefined = process.env.NEXT_PUBLIC_SITE_URL,
): string | null {
  if (!siteUrl) {
    return null;
  }

  let siteHostname: string;
  try {
    siteHostname = new URL(siteUrl).hostname.toLowerCase();
  } catch {
    return null;
  }

  const registrable = siteHostname.startsWith("www.") ? siteHostname.slice(4) : siteHostname;

  // A single-label host (`localhost`) is not a domain browsers reliably accept
  // a `domain=` attribute for, and there is nothing to share it with anyway.
  if (!registrable.includes(".")) {
    return null;
  }

  const host = hostname.toLowerCase();
  if (host !== registrable && !host.endsWith(`.${registrable}`)) {
    return null;
  }

  return registrable;
}

function readConsentCookie(): string | null {
  const target = `${CONSENT_STORAGE_KEY}=`;

  for (const entry of document.cookie.split(";")) {
    const trimmed = entry.trim();
    if (trimmed.startsWith(target)) {
      // An empty value is an expired/cleared cookie, not a decision. Returning
      // "" here would mask the localStorage mirror, since `??` only falls
      // through on null.
      return decodeURIComponent(trimmed.slice(target.length)) || null;
    }
  }

  return null;
}

function writeConsentCookie(serialized: string): void {
  const domain = resolveConsentCookieDomain(window.location.hostname);
  const parts = [
    `${CONSENT_STORAGE_KEY}=${encodeURIComponent(serialized)}`,
    "path=/",
    `max-age=${CONSENT_COOKIE_MAX_AGE_SECONDS}`,
    // Lax, not Strict: the buyer returns from Shopify checkout on another
    // origin, and a Strict cookie would be withheld on that navigation and
    // re-prompt them.
    "SameSite=Lax",
  ];

  if (domain) {
    parts.push(`domain=${domain}`);
  }
  if (window.location.protocol === "https:") {
    parts.push("Secure");
  }

  document.cookie = parts.join("; ");
}

function isConsentValue(raw: unknown): raw is ConsentValue {
  return raw === "granted" || raw === "denied";
}

function parseState(raw: string | null): ConsentState | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    if (isConsentValue(record.analytics) && isConsentValue(record.marketing)) {
      return { analytics: record.analytics, marketing: record.marketing };
    }
  } catch {
    // Not valid JSON — treat as no decision.
  }
  return null;
}

function computeState(raw: string | null, legacy: string | null): ConsentState | null {
  const parsed = parseState(raw);
  if (parsed) {
    return parsed;
  }
  // Migrate a pre-marketing (v1) decision: keep the analytics choice the user
  // already made, and default marketing to denied since they were never
  // offered it. The v1 key is cleared on the next `setConsent`.
  if (isConsentValue(legacy)) {
    return { analytics: legacy, marketing: "denied" };
  }
  return null;
}

// `getConsent` backs a `useSyncExternalStore` snapshot, which requires a
// referentially-stable value between calls — returning a fresh object every
// time triggers an infinite render loop. Cache the parsed result and only mint
// a new object when the underlying raw strings actually change.
let cachedRaw: string | null = null;
let cachedLegacy: string | null = null;
let cachedState: ConsentState | null = null;
let cachePrimed = false;

export function getConsent(): ConsentState | null {
  if (typeof window === "undefined") {
    return null;
  }

  // Cookie first: it is the cross-host record, so a decision made on either
  // host wins over a stale same-origin mirror. Reading `document.cookie` is
  // synchronous, which matters — `instrumentation-client.ts` calls this before
  // `posthog.init` to choose consent-aware persistence, and that pre-init read
  // is what keeps identity alive across the cart's hard navigation (PR #56).
  let cookieRaw: string | null = null;
  try {
    cookieRaw = readConsentCookie();
  } catch {
    // Cookies unavailable (sandboxed iframe). Fall through to localStorage.
  }

  let raw: string | null = null;
  let legacy: string | null = null;
  let localStorageThrew = false;
  try {
    raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    legacy = window.localStorage.getItem(LEGACY_CONSENT_STORAGE_KEY);
  } catch {
    // localStorage can throw in private-mode Safari and locked-down embeds.
    // A cookie may still carry the decision, so this is not fatal on its own.
    localStorageThrew = true;
  }

  const effectiveRaw = cookieRaw ?? raw;
  if (cachePrimed && effectiveRaw === cachedRaw && legacy === cachedLegacy) {
    return cachedState;
  }

  cachedRaw = effectiveRaw;
  cachedLegacy = legacy;
  cachedState = computeState(effectiveRaw, legacy);
  cachePrimed = true;

  // Migration, not a reset: a visitor who decided before consent moved to a
  // cookie keeps that choice, promoted in place to the cross-host record so
  // they are never re-prompted — on either host. Deliberately silent (no
  // change event): nothing about their decision changed, only where it lives.
  if (!cookieRaw && cachedState && !localStorageThrew) {
    try {
      writeConsentCookie(JSON.stringify(cachedState));
    } catch {
      // Best effort. The localStorage mirror still answers on this host.
    }
  }

  return cachedState;
}

export function setConsent(state: ConsentState): void {
  if (typeof window === "undefined") {
    return;
  }
  const serialized = JSON.stringify(state);

  // Both stores, every time. The cookie is what crosses hosts; the
  // localStorage mirror is what still answers when a cookie write is refused
  // (ITP-style restrictions, embedded webviews). Neither one failing is a
  // reason to skip the other, or to skip notifying subscribers.
  try {
    writeConsentCookie(serialized);
  } catch {
    // Cookies unavailable — the mirror below still holds the decision.
  }

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, serialized);
    // Drop the superseded v1 key so migration doesn't shadow later changes.
    window.localStorage.removeItem(LEGACY_CONSENT_STORAGE_KEY);
  } catch {
    // Persist failed (private mode, quota). Still notify in-tab subscribers
    // so the SDKs toggle for the current session even if the choice can't
    // be remembered.
  }
  const detail: ConsentChangeDetail = { state };
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail }));
}
